/**
 * Export the whole timeline to a video file: mix audio, plan the encoder,
 * composite every frame into the sink's canvas, encode, mux.
 *
 * Frame count is exactly round(durationMs / 1000 * fps) and frame i is
 * stamped i * 1e6 / fps µs; audio sample j is stamped j * 1e6 / sampleRate µs.
 * Both are anchored at 0 and neither comes from the wall clock, so A/V sync
 * is exact by construction (the MediaRecorder sink paces itself instead,
 * `realtime: true`).
 *
 * Audio chunks are fed alongside the frames they belong to, so the muxers
 * receive interleaved, time-ordered tracks and only one frame's worth of
 * AudioData exists at a time.
 *
 * Cancellation is one AbortSignal, checked at the top of every frame and
 * inside the sinks' backpressure waits.
 */

import type { ProjectPage, VideoExportSettings, VideoProject } from '@canvas/shared-types';
import type { ReelProgress } from '@/lib/reelProgress';
import { createAudioChunkReader, renderTimelineAudio, type AudioChunkReader, type RenderedAudio } from './audioMixer';
import { applyPlan, planEncoder, type EncoderPlan } from './encoderCapabilities';
import { abortError } from './exportErrors';
import { createFrameCompositor, type FrameCompositor } from './frameCompositor';
import { exportFrameCount, frameTimestampMicros } from './frameTiming';
import { totalDurationMs } from './sceneTimeline';
import { audioClipsToTracks, placedVideoClips } from './timelineEdit';
import { clipAudible, timelineClips } from './videoClip';
import { createVideoEncoderSink, type EncodedVideoResult, type VideoEncoderSink } from './videoEncoder';
import { bandPercent, etaHint, videoProgressAt } from './videoProgress';

/** Frames between yields to the event loop, so the tab stays responsive. */
const YIELD_EVERY_FRAMES = 30;
/** Frames between progress updates (cheap, but React re-renders on each). */
const PROGRESS_EVERY_FRAMES = 4;

const NO_AUDIO_ENCODER_COPY =
  'This browser can’t encode audio for this format, so the video was exported without sound.';

function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

/** Feed every audio chunk stamped before `untilMicros`; close any left over if the sink throws. */
async function feedAudio(sink: VideoEncoderSink, reader: AudioChunkReader, untilMicros: number): Promise<void> {
  const batch = reader.take(untilMicros);
  try {
    while (batch.length) await sink.addAudio?.(batch.shift() as AudioData);
  } finally {
    batch.forEach((d) => d.close());
  }
}

/**
 * Play the mixed buffer into a live MediaStream track for the real-time
 * recorder, starting exactly when recording does. Returns a disposer.
 */
function attachLiveAudio(sink: VideoEncoderSink, audio: RenderedAudio): () => void {
  const ctx = new AudioContext({ sampleRate: audio.sampleRate });
  const source = ctx.createBufferSource();
  source.buffer = audio.buffer;
  const dest = ctx.createMediaStreamDestination();
  source.connect(dest);
  sink.attachAudioTrack?.(dest.stream.getAudioTracks()[0], () => {
    void ctx.resume();
    source.start();
  });
  return () => void ctx.close().catch(() => undefined);
}

export async function exportVideo(args: {
  pages: ProjectPage[];
  video: VideoProject;
  settings: VideoExportSettings;
  signal: AbortSignal;
  onProgress: (p: ReelProgress) => void;
  /** A plan already computed (and shown) by the dialog. */
  plan?: EncoderPlan;
  /** Disk target for a large MP4. Chosen by the dialog from a user gesture. */
  fileHandle?: FileSystemFileHandle;
}): Promise<EncodedVideoResult> {
  const { pages, video, signal, onProgress } = args;
  const startedAt = Date.now();
  const progress = (p: ReelProgress) => onProgress({ ...p, startedAt });

  progress(videoProgressAt('prepare', { percent: 0, label: 'Preparing scenes…' }));
  const durationMs = video.v === 2 ? video.totalDurationMs : totalDurationMs(video.scenes);
  if (durationMs <= 0) {
    throw new Error(video.v === 2 ? 'Add at least one clip before exporting.' : 'Add at least one scene before exporting.');
  }

  const plan = args.plan ?? (await planEncoder(args.settings));
  const settings = applyPlan(args.settings, plan);
  const total = exportFrameCount(durationMs, settings.fps);
  const notes: string[] = [];

  let audio: RenderedAudio | null = null;
  const clips = [
    ...timelineClips(pages, video),
    ...placedVideoClips(video),
  ].filter((c) => clipAudible(c.clip));
  const musicTracks = video.v === 2 ? audioClipsToTracks(video) : [];
  if (settings.includeAudio && (video.v === 2 ? musicTracks.length || clips.length : video.audio || clips.length)) {
    progress(videoProgressAt('prepare', { percent: bandPercent('prepare', 0.25), label: 'Mixing audio…' }));
    const mix = await renderTimelineAudio({
      track: video.v === 2 ? null : video.audio,
      tracks: musicTracks,
      clips,
      durationMs,
      sampleRate: settings.audioSampleRate,
      channels: 2,
      signal,
    });
    audio = mix.audio;
    if (mix.warning) notes.push(mix.warning);
    if (audio && plan.backend === 'webcodecs' && !plan.audioCodecString) {
      notes.push(NO_AUDIO_ENCODER_COPY);
      audio = null;
    }
  }

  let sink: VideoEncoderSink | null = null;
  let compositor: FrameCompositor | null = null;
  let disposeLiveAudio: (() => void) | null = null;
  let finalized = false;
  try {
    sink = await createVideoEncoderSink({
      settings,
      durationMs,
      signal,
      plan,
      audio:
        audio && plan.backend === 'webcodecs'
          ? { sampleRate: audio.sampleRate, numberOfChannels: audio.numberOfChannels }
          : undefined,
      fileHandle: args.fileHandle,
    });
    const reader = audio && sink.addAudio ? createAudioChunkReader(audio) : null;
    if (audio && sink.attachAudioTrack) disposeLiveAudio = attachLiveAudio(sink, audio);

    progress(videoProgressAt('prepare', { percent: bandPercent('prepare', 0.5) }));
    compositor = await createFrameCompositor({ pages, video, target: sink.canvas, signal });

    const renderStarted = performance.now();
    for (let i = 0; i < total; i += 1) {
      if (signal.aborted) throw abortError();
      await compositor.renderFrameAt((i * 1000) / settings.fps);
      const keyFrame = i % Math.max(1, settings.keyFrameInterval) === 0;
      await sink.addFrame(i, frameTimestampMicros(i, settings.fps), keyFrame);
      if (reader) await feedAudio(sink, reader, frameTimestampMicros(i + 1, settings.fps));
      if (i % PROGRESS_EVERY_FRAMES === 0) {
        progress(
          videoProgressAt('render', {
            percent: bandPercent('render', i / total),
            label: `Rendering frame ${i + 1} of ${total}…`,
            etaHint: etaHint(i, total, performance.now() - renderStarted),
          })
        );
      }
      if (!sink.realtime && i % YIELD_EVERY_FRAMES === 0) await yieldToBrowser();
    }
    if (reader) await feedAudio(sink, reader, Number.POSITIVE_INFINITY);

    progress(videoProgressAt('encode', { percent: bandPercent('encode', 0.5) }));
    progress(videoProgressAt('mux', { percent: bandPercent('mux', 0.5) }));
    const result = await sink.finalize();
    finalized = true;
    const note = [result.note, ...notes, ...compositor.warnings()].filter(Boolean).join(' ');
    return { ...result, note: note || undefined };
  } finally {
    compositor?.dispose();
    disposeLiveAudio?.();
    if (sink && !finalized) await sink.abort(signal.aborted ? 'cancelled' : 'failed');
  }
}
